
// ==============================================
// Umap.jsx — UMAP Dimensionality Reduction Page (with persistence)
// ==============================================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useProject } from "../ProjectContext";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { 
  umap_generation, 
  umap_calculate_eigenvalues_service,
  check_umap_output_api,
  subscribe_task_stream
} from "../api/project.api";

// ========== Main UMAP Component ==========
const Umap = () => {
  // --- Context & Navigation ---
  const { user, setRedirectPath, userId } = useAuth();
  const { activeProject } = useProject();
  const navigate = useNavigate();

  // --- UI State ---
  const [reduceStatus, setReduceStatus] = useState("idle"); // 'idle' | 'reducing' | 'reduced'
  const [infoMessage, setInfoMessage] = useState("");
  const [points, setPoints] = useState([]);
  const [texts, setTexts] = useState([]);
  const [eigenvalues, setEigenvalues] = useState([]);
  const [umapOutputExists, setUmapOutputExists] = useState(false);
  const [generateGraph, setGenerateGraph] = useState(false);
  const [isCheckingExisting, setIsCheckingExisting] = useState(true); // ← Add loading state

  // -------- Auth Guard --------
  useEffect(() => {
    if (!user) {
      setRedirectPath("/project/umap");
      navigate("/login");
    }
  }, [user, navigate, setRedirectPath]);

  // -------- Check for Existing UMAP Output on Mount --------
  useEffect(() => {
    const checkExistingUmapOutput = async () => {
      if (!userId || !activeProject?.ProjectID) {
        setIsCheckingExisting(false);
        return;
      }

      try {
        setIsCheckingExisting(true);
        const response = await check_umap_output_api(userId, activeProject.ProjectID);
        // Check if UMAP data exists
        if (response.points && response.points.length > 0) {
          // Restore UMAP state
          setPoints(response.points);
          setReduceStatus("reduced");
          setUmapOutputExists(true);
          setGenerateGraph(true); // ← Show graph automatically
          
          // Check if eigenvalues also exist
          if (response.eigenvalues && response.eigenvalues.length > 0) {
            setEigenvalues(response.eigenvalues);
            setInfoMessage("Loaded existing UMAP projection and eigenvalues.");
          } else {
            setInfoMessage("Loaded existing UMAP projection.");
          }
        } else {
          // No UMAP data found - fresh start
          setReduceStatus("idle");
          setUmapOutputExists(false);
          setGenerateGraph(false);
          setPoints([]);
          setEigenvalues([]);
        }
      } catch (error) {
        // Don't show error to user - just start fresh
        setReduceStatus("idle");
        setUmapOutputExists(false);
        setGenerateGraph(false);
      } finally {
        setIsCheckingExisting(false);
      }
    };

    checkExistingUmapOutput();
  }, [userId, activeProject?.ProjectID]); // Re-check when user or project changes

  // -------- Reset on project change (moved after persistence check) --------
  // Note: The persistence check useEffect handles project changes now
  // This useEffect is no longer needed as it conflicts with persistence
  // useEffect(() => {
  //   setReduceStatus("idle");
  //   setInfoMessage("");
  //   setPoints([]);
  //   setTexts([]);
  //   setEigenvalues([]);
  //   setUmapOutputExists(false);
  // }, [activeProject]);

  // -------- UMAP Reduction Handler --------
  const handleUmapReduce = async () => {
    if (!user || !activeProject) {
      setInfoMessage("Missing user or active project.");
      return;
    }
    setReduceStatus("reducing");
    setInfoMessage("Reducing with UMAP...");
    setPoints([]);
    setTexts([]);
    setEigenvalues([]);

    try {
      const start = await umap_generation(userId, activeProject.ProjectID);
      const taskId = start?.task_id;
      if (!taskId) throw new Error("No task id returned");

      subscribe_task_stream(taskId, (evt) => {
        if (evt.state === 'PROCESSING') {
          setInfoMessage(evt.status || 'Reducing with UMAP...');
        }
        if (evt.state === 'SUCCESS') {
          const payload = evt.result?.data || evt.result || {};
          if (payload.points) {
            setReduceStatus("reduced");
            setInfoMessage("UMAP reduction complete.");
            setPoints(payload.points);
            setUmapOutputExists(true);
            activeProject.currentStage = "umap";
          } else {
            setReduceStatus("idle");
            setInfoMessage("UMAP reduction completed without points data.");
            setUmapOutputExists(false);
          }
        }
        if (evt.state === 'FAILURE') {
          setReduceStatus("idle");
          setInfoMessage("UMAP reduction failed.");
          setUmapOutputExists(false);
        }
      });
    } catch (err) {
      setReduceStatus("idle");
      setInfoMessage("UMAP processing failed.");
      setUmapOutputExists(false);
    }
  };

  // -------- Graph Generation Handler --------
  const handleGenerateGraph = async () => {
    setInfoMessage("Generating graph...");

    try {
      setGenerateGraph(true);
      setInfoMessage("Graph generated.");
    } catch (err) {
      setInfoMessage("Failed to generate graph.");
    }
  };

  // -------- Eigenvalue Calculation Handler --------
  const handleEigenCalculation = async () => {
    setInfoMessage("Calculating eigenvalues...");
    try {
      const start = await umap_calculate_eigenvalues_service(userId, activeProject.ProjectID);
      const taskId = start?.task_id;
      if (!taskId) throw new Error("No task id returned");

      subscribe_task_stream(taskId, (evt) => {
        if (evt.state === 'PROCESSING') {
          setInfoMessage(evt.status || 'Calculating eigenvalues...');
        }
        if (evt.state === 'SUCCESS') {
          const payload = evt.result?.data || evt.result || {};
          if (payload.eigenvalues) {
            setEigenvalues(payload.eigenvalues);
            setInfoMessage("Eigenvalues Generated successfully");
          } else {
            setInfoMessage("Eigenvalues task completed without data.");
          }
        }
        if (evt.state === 'FAILURE') {
          setInfoMessage("Failed to calculate eigenvalues.");
        }
      });
    } catch (err) {
      setInfoMessage("Failed to calculate eigenvalues.");
    }
  };

  const goToPCAAnalysis = () => {
    navigate("/project/pca");
  };

  // ========== Loading State ==========
  if (isCheckingExisting) {
    return (
      <div className="flex flex-col h-full w-full bg-[#f9f9f9]">
        <div className="w-full bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
          <div className="px-4 sm:px-6 md:px-8 py-3 sm:py-4">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">UMAP Dimensionality Reduction</h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#ff2e2e] mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Checking for existing UMAP data...</p>
          </div>
        </div>
      </div>
    );
  }

  // ========== Render ==========
  return (
    <div className="flex flex-col h-full w-full bg-[#f9f9f9]">
      {/* Outlet Header */}
      <div className="w-full bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-4 sm:px-6 md:px-8 py-3 sm:py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">UMAP Dimensionality Reduction</h1>
          <button
            onClick={goToPCAAnalysis}
            className="w-full sm:w-auto px-4 sm:px-6 md:px-9 py-2.5 sm:py-3 bg-[#ff2e2e] text-white font-bold text-sm sm:text-base md:text-lg rounded-xl shadow-[0_6px_22px_rgba(255,46,46,0.16)] hover:bg-[#e60000] transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            disabled={!eigenvalues.length > 0}
          >
            PCA Analysis
          </button>
        </div>
      </div>

      {/* Content Area - fills remaining space */}
      <div className="flex-1 overflow-auto">
        {!generateGraph ? (
          // Original blocks view when no graph
          <div className="h-full flex items-center justify-center p-4 sm:p-6 md:p-8 lg:p-10">
            {/* Blocks Container - Stack vertically on mobile, side-by-side on lg+ */}
            <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-10">

              {/* ========== UMAP Processing Block ========== */}
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 border border-gray-200 flex flex-col min-h-[400px] sm:min-h-[450px] lg:h-[55vh]">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">UMAP Processing</h2>
                  <span className="text-xs sm:text-sm font-semibold text-gray-600 bg-gray-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded whitespace-nowrap">
                    UMAP Algorithm
                  </span>
                </div>

                {/* Description */}
                <p className="text-gray-700 text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed flex-grow">
                  Reduce high-dimensional embedding data to 2D visualization space using UMAP
                  (Uniform Manifold Approximation and Projection). This algorithm preserves both local and global
                  data structure for meaningful visualizations.
                </p>

                {/* Reduce UMAP Button */}
                <button
                  className={`
                    w-full px-4 sm:px-6 md:px-8 py-3 sm:py-4 border-none rounded-md cursor-pointer 
                    font-bold text-sm sm:text-base md:text-lg transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleUmapReduce}
                  disabled={reduceStatus === "reducing"}
                >
                  {reduceStatus === "idle" && "Reduce UMAP"}
                  {reduceStatus === "reducing" && "Reducing..."}
                  {reduceStatus === "reduced" && "Reduced ✓"}
                </button>

                {/* Status Message */}
                {(infoMessage) && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded text-base text-blue-800">
                    {infoMessage}
                  </div>
                )}
              </div>

              {/* ========== Visualization & Analysis Block ========== */}
              <div
                className={`
                  bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 border border-gray-200 flex flex-col min-h-[400px] sm:min-h-[450px] lg:h-[55vh]
                  ${!umapOutputExists ? 'opacity-50 pointer-events-none' : ''}
                `}
              >
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">Visualization & Analysis</h2>
                  {!umapOutputExists && (
                    <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-4 py-2 rounded">
                      Locked
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="text-gray-700 text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed flex-grow">
                  Generate interactive scatter plots to visualize your reduced data and calculate eigenvalues
                  to understand the variance captured by each dimension in your UMAP projection.
                </p>

                {/* Generate Graph Button */}
                <button
                  className={`
                    w-full px-4 sm:px-6 md:px-8 py-3 sm:py-4 border-none rounded-md cursor-pointer 
                    font-bold text-sm sm:text-base md:text-lg transition-all duration-200 text-center mb-3 sm:mb-4
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleGenerateGraph}
                  disabled={!umapOutputExists}
                >
                  Generate Graph
                </button>

                {/* Eigenvalues Button */}
                <button
                  className={`
                    w-full px-4 sm:px-6 md:px-8 py-3 sm:py-4 border-none rounded-md cursor-pointer 
                    font-bold text-sm sm:text-base md:text-lg transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleEigenCalculation}
                  disabled={!umapOutputExists}
                >
                  {eigenvalues.length === 0 && "Calculate Eigenvalues"}
                  {eigenvalues.length > 0 && "Eigenvalues Calculated ✓"}
                </button>

                {!umapOutputExists && (
                  <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded text-base text-gray-600 text-center">
                    Complete UMAP reduction first to unlock visualization
                  </div>
                )}
              </div>

            </div>
          </div>
        ) : (
          // Graph view with collapsed buttons
          <div className="w-full h-full flex flex-col">
            {/* Collapsed Buttons Row */}
            <div className="w-full bg-white border-b border-gray-200 px-8 py-4">
              <div className="max-w-7xl mx-auto flex flex-wrap gap-4 justify-center">
                <button
                  className={`
                    px-6 py-3 border-none rounded-md cursor-pointer 
                    font-bold text-base transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleUmapReduce}
                  disabled={reduceStatus === "reducing"}
                >
                  {reduceStatus === "idle" && "Reduce UMAP"}
                  {reduceStatus === "reducing" && "Reducing..."}
                  {reduceStatus === "reduced" && "Reduced ✓"}
                </button>

                <button
                  className={`
                    px-6 py-3 border-none rounded-md cursor-pointer 
                    font-bold text-base transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleGenerateGraph}
                  disabled={!umapOutputExists}
                >
                  Graph Generated ✓
                </button>

                <button
                  className={`
                    px-6 py-3 border-none rounded-md cursor-pointer 
                    font-bold text-base transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleEigenCalculation}
                  disabled={!umapOutputExists}
                >
                  {eigenvalues.length === 0 && "Calculate Eigenvalues"}
                  {eigenvalues.length > 0 && "Eigenvalues Calculated ✓"}
                </button>
              </div>
            </div>

            {/* Graph and Results Display */}
            <div className="flex-1 overflow-auto p-8">
              <div className="max-w-7xl mx-auto">
                {/* Eigenvalue Output */}
                {eigenvalues.length > 0 && (
                  <div className="mb-6 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl shadow-lg">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Eigenvalues</h3>
                    <div className="flex gap-4 justify-center">
                      {eigenvalues.map((value, index) => (
                        <div
                          key={index}
                          className="flex-1 p-6 bg-white border-2 border-emerald-400 rounded-2xl shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
                        >
                          <div className="text-xs text-emerald-600 font-semibold mb-2 uppercase tracking-wider">
                            Eigenvalue {index + 1}
                          </div>
                          <div className="text-4xl font-mono font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Scatter Plot */}
                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                  <h3 className="text-xl font-bold text-[#0c0c1d] mb-4">UMAP Projection</h3>
                  <div style={{ width: "100%", height: 600 }}>
                    <ResponsiveContainer>
                      <ScatterChart
                        margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                      >
                        <XAxis type="number" dataKey="x" name="X" />
                        <YAxis type="number" dataKey="y" name="Y" />
                        <Tooltip
                          cursor={{ strokeDasharray: "3 3" }}
                          content={({ active, payload }) => {
                            if (active && payload?.length) {
                              const point = payload[0].payload;
                              const text = point.text || "No text";
                              return (
                                <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                                  <p className="font-bold text-gray-900">{point.name}</p>
                                  <p className="text-gray-700 text-sm">
                                    X: {point.x.toFixed(2)} | Y: {point.y.toFixed(2)}
                                  </p>
                                  <p className="text-gray-600 text-sm mt-2">{text}</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Scatter data={points} fill="#8884d8">
                          <LabelList
                            dataKey="index"
                            position="left"
                            style={{ fontSize: 8, fill: "#333" }}
                          />
                        </Scatter>
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Info Message */}
                {infoMessage && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded text-base text-blue-800">
                    {infoMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Umap;
// ==============================================